// components/MultiCompetitionTimer.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import {
    Play,
    Pause,
    RotateCcw,
    Clock,
    Users,
    Trophy,
    Calendar,
    MapPin,
    Trash2,
    Plus,
    ExternalLink,
    ChevronRight,
    Check,
    Copy
} from 'lucide-react';
import {
    getCompetitionsWithTimer,
    createCompetitionGroup,
    getCompetitionGroups,
    deleteCompetitionGroup,
    pauseTimer,
    resumeTimer,
    resetTimer,
    startClimbing,
    startWrapTime,
    startCountdown,
    createTimer
} from '../services/apiServices';
import socketService from '../services/socketService';
import { StartDialog } from './StartDialog';

const MultiCompetitionTimer = () => {
    const navigate = useNavigate();

    const [competitions, setCompetitions] = useState([]);
    const [competitionGroups, setCompetitionGroups] = useState([]);
    const [selectedCompetitions, setSelectedCompetitions] = useState([]);
    const [showSelectionDialog, setShowSelectionDialog] = useState(false);
    const [showCountdownDialog, setShowCountdownDialog] = useState(false);
    const [countdownDuration, setCountdownDuration] = useState(10);
    const [isSocketConnected, setIsSocketConnected] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [groupLoading, setGroupLoading] = useState({});
    const [isFetching, setIsFetching] = useState(false);

    // Simplified fetch - only basic data, every 3 minutes
    const fetchData = useCallback(async () => {
        if (isFetching) return;

        setIsFetching(true);
        setError(null);

        try {
            const loggedAdminInfo = localStorage.getItem("loggedAdminInfo");
            if (!loggedAdminInfo) {
                setError("Please login to access this feature");
                setIsFetching(false);
                return;
            }

            const organizerInfo = JSON.parse(loggedAdminInfo);
            const organizerId = organizerInfo?.organizerId;

            if (!organizerId) {
                setError("Organizer ID not found in session");
                setIsFetching(false);
                return;
            }

            console.log("Fetching basic data for organizer:", organizerId);

            // Fetch only competitions (basic data - no timer details needed)
            try {
                const compsResponse = await getCompetitionsWithTimer(organizerId);
                if (compsResponse && compsResponse.ok) {
                    const compsData = await compsResponse.json();
                    // Only store basic competition info we need
                    const basicCompetitions = compsData.map(comp => ({
                        _id: comp._id,
                        name: comp.name,
                        status: comp.status,
                        competitors: comp.competitors || [],
                        numberofboulders: comp.numberofboulders || 0,
                        startDate: comp.startDate,
                        location: comp.location,
                        organizerId: comp.organizerId,
                        climbTime: comp.climbTime,
                        warningTime: comp.warningTime,
                        wrapTime: comp.wrapTime,
                        // Only minimal timer info if it exists
                        timer: comp.timer ? {
                            status: comp.timer.status,
                            currentPhase: comp.timer.currentPhase
                        } : null
                    }));
                    setCompetitions(basicCompetitions);
                    console.log(`Fetched ${basicCompetitions.length} competitions (basic info only)`);
                }
            } catch (compError) {
                console.warn("Error fetching competitions:", compError.message);
            }

            // Fetch competition groups
            try {
                const groupsResponse = await getCompetitionGroups(organizerId);
                if (groupsResponse && groupsResponse.ok) {
                    const groupsData = await groupsResponse.json();

                    if (groupsData.success) {
                        setCompetitionGroups(groupsData.data || []);
                        console.log(`Fetched ${groupsData.data?.length || 0} groups`);
                    }
                }
            } catch (groupError) {
                console.warn("Error fetching groups:", groupError.message);
            }
        } catch (err) {
            console.error("Error in fetchData:", err);
            // Only show critical errors
            if (err.message.includes("login") || err.message.includes("session")) {
                setError(err.message);
            }
        } finally {
            setIsFetching(false);
        }
    }, [isFetching]);

    useEffect(() => {
        fetchData();

        // Poll every 3 minutes (180,000 ms) as requested
        const interval = setInterval(fetchData, 180000);
        return () => clearInterval(interval);
    }, [fetchData]);

    // Initialize WebSocket - simplified, just for connection status
    useEffect(() => {
        let isMounted = true;

        const initSocket = async () => {
            try {
                await socketService.connect();
                if (isMounted) {
                    setIsSocketConnected(true);
                }
            } catch (error) {
                console.error('WebSocket connection error:', error);
                if (isMounted) setIsSocketConnected(false);
            }
        };

        initSocket();

        return () => {
            isMounted = false;
            socketService.disconnect();
        };
    }, []);

    // Handle competition selection for new group
    const handleCompetitionSelect = (competitionId) => {
        setSelectedCompetitions(prev => {
            if (prev.includes(competitionId)) {
                return prev.filter(id => id !== competitionId);
            } else {
                return [...prev, competitionId];
            }
        });
    };

    // Start new group creation
    const handleStartNewGroup = () => {
        setSelectedCompetitions([]);
        setShowSelectionDialog(true);
    };

    // Create new competition group
    const handleCreateGroup = () => {
        if (selectedCompetitions.length === 0) {
            setError('Please select at least one competition');
            return;
        }

        setShowSelectionDialog(false);
        setShowCountdownDialog(true);
    };

    // Start countdown for the group - simplified
    const handleStartGroupCountdown = async (
        countdownSeconds
    ) => {
        setLoading(true);
        setError(null);

        try {
            const loggedAdminInfo = localStorage.getItem("loggedAdminInfo");
            if (!loggedAdminInfo) {
                throw new Error('Please login again.');
            }

            const organizerInfo = JSON.parse(loggedAdminInfo);
            const organizerId = organizerInfo?.organizerId;

            if (!organizerId) {
                throw new Error('No organizer ID found');
            }

            if (!selectedCompetitions || selectedCompetitions.length === 0) {
                throw new Error('No competitions selected');
            }

            console.log('Creating group with', selectedCompetitions.length, 'competitions');

            // Create competition group
            const groupResponse = await createCompetitionGroup(organizerId, {
                name: `Group ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
                competitionIds: selectedCompetitions,
                countdownDuration: countdownSeconds
            });

            if (!groupResponse || groupResponse === "false") {
                throw new Error('Failed to create group: No response from server');
            }

            if (!groupResponse.ok) {
                let errorMessage = 'Failed to create group';
                try {
                    const errorData = await groupResponse.json();
                    errorMessage = errorData.message || `HTTP ${groupResponse.status}`;
                } catch (e) {
                    errorMessage = `HTTP ${groupResponse.status}`;
                }
                throw new Error(errorMessage);
            }

            const groupData = await groupResponse.json();

            if (!groupData.success) {
                throw new Error(groupData.message || 'Failed to create group');
            }

            console.log('Group created successfully');

            // Start timers with minimal error handling
            const timerPromises = selectedCompetitions.map(async (competitionId, index) => {
                try {
                    await new Promise(resolve => setTimeout(resolve, index * 300));

                    const competition = competitions.find(c => c._id === competitionId);
                    if (!competition) return { success: false };
                    console.log(competitionId)
                    console.log(competitions)
                    console.log(competition, 'testing')

                    console.log({
                        countdown: countdownSeconds,
                        climbTime: competition.climbTime || 300,
                        warningTime: competition.warningTime || 60,
                        wrapTime: competition.wrapTime || 120,
                        organizerId: competition.organizerId || organizerId
                    })
                    // Create timer
                    const timerResponse = await createTimer(competitionId, {
                        countdown: countdownSeconds,
                        climbTime: competition.climbTime || 300,
                        warningTime: competition.warningTime || 60,
                        wrapTime: competition.wrapTime || 120,
                        organizerId: competition.organizerId || organizerId
                    });

                    if (!timerResponse || timerResponse === "false") {
                        return { success: false };
                    }

                    if (!timerResponse.ok) return { success: false };

                    // Start countdown
                    await new Promise(resolve => setTimeout(resolve, 300));
                    await startCountdown(competitionId);

                    return { success: true };

                } catch (error) {
                    console.warn(`Timer error for competition ${competitionId}:`, error);
                    return { success: false };
                }
            });

            const results = await Promise.allSettled(timerPromises);
            const successful = results.filter(r =>
                r.status === 'fulfilled' && r.value?.success
            ).length;
            console.log(results, 'results')

            // Refresh data once
            await fetchData();

            setSelectedCompetitions([]);
            setShowCountdownDialog(false);

            if (successful > 0) {
                setError(`✅ Group created! ${successful} competition(s) started.`);
                setTimeout(() => setError(null), 3000);
            }

        } catch (error) {
            console.error('Failed to create group:', error);
            setError(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Control all competitions in a group - simplified
    const handleControlGroup = async (group, action) => {
        setGroupLoading(prev => ({ ...prev, [group._id]: true }));
        setError(null);

        try {
            const controlPromises = group.competitionIds.map(async (competitionId) => {
                try {
                    let response;
                    switch (action) {
                        case 'pause':
                            response = await pauseTimer(competitionId, "Group control", "Admin");
                            break;
                        case 'resume':
                            response = await resumeTimer(competitionId);
                            break;
                        case 'reset':
                            response = await resetTimer(competitionId);
                            break;
                        case 'climbing':
                            response = await startClimbing(competitionId);
                            break;
                        case 'wrap_time':
                            response = await startWrapTime(competitionId);
                            break;
                        default:
                            return { success: false };
                    }

                    return { success: response && response.ok };
                } catch (error) {
                    console.warn(`Control error for ${competitionId}:`, error);
                    return { success: false };
                }
            });

            await Promise.allSettled(controlPromises);

            // Refresh once after all controls
            await fetchData();

        } catch (error) {
            setError(`Failed to ${action} group: ${error.message}`);
        } finally {
            setGroupLoading(prev => ({ ...prev, [group._id]: false }));
        }
    };

    // Delete competition group
    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm('Are you sure you want to delete this group? This will not delete the competitions.')) {
            return;
        }

        setGroupLoading(prev => ({ ...prev, [groupId]: true }));

        try {
            const response = await deleteCompetitionGroup(groupId);

            if (!response || !response.ok) {
                throw new Error('Failed to delete group');
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || 'Failed to delete group');
            }

            // Remove from local state
            setCompetitionGroups(prev => prev.filter(group => group._id !== groupId));

        } catch (error) {
            setError(`Failed to delete group: ${error.message}`);
        } finally {
            setGroupLoading(prev => ({ ...prev, [groupId]: false }));
        }
    };

    // Get status color
    const getStatusColor = (status) => {
        if (!status) return 'bg-gray-500';

        switch (status.toLowerCase()) {
            case 'countdown':
            case 'ready':
                return 'bg-blue-500';
            case 'climbing':
            case 'running':
                return 'bg-green-500';
            case 'warning':
                return 'bg-orange-500';
            case 'wrap_time':
                return 'bg-purple-500';
            case 'paused':
                return 'bg-yellow-500';
            case 'completed':
            case 'finished':
                return 'bg-gray-500';
            default:
                return 'bg-gray-500';
        }
    };

    const getStatusText = (status) => {
        if (!status) return 'UNKNOWN';
        return status.toUpperCase();
    };

    // Get competition details by ID
    const getCompetitionById = (id) => {
        return competitions.find(c => c._id === id);
    };

    // Get group status - simplified
    const getGroupStatus = (group) => {
        const groupComps = group.competitionIds
            .map(id => getCompetitionById(id))
            .filter(Boolean);

        if (groupComps.length === 0) return 'empty';

        // Simple status check
        const anyRunning = groupComps.some(comp =>
            comp.timer?.status && ['climbing', 'running', 'countdown'].includes(comp.timer.status.toLowerCase())
        );
        if (anyRunning) return 'running';

        const anyPaused = groupComps.some(comp =>
            comp.timer?.status === 'paused'
        );
        if (anyPaused) return 'paused';

        return 'ready';
    };

    const [copiedTimer, setCopiedTimer] = useState(false);
    const [copiedResults, setCopiedResults] = useState(false);
    // Simplified group card - only basic info
    const renderGroupCard = (group) => {
        const groupStatus = getGroupStatus(group);
        const isLoading = groupLoading[group._id];
        // console.log(group);

        const timerUrl = `${window.location.origin}/group-timer/${group._id}`;
        const copyToClipboard = (text, type) => {
            navigator.clipboard.writeText(text);
            setCopiedResults(true);
            setTimeout(() => setCopiedResults(false), 2000);

        };
        const hasWrapTimeCompetitions = group.competitionIds.some(competitionId => {
            const competition = getCompetitionById(competitionId);
            return competition?.timer?.currentPhase?.toLowerCase() === 'wrap_time';
        });

        return (
            <Card key={group._id} className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <CardTitle className="text-xl">{group.name}</CardTitle>
                                <Badge className={`${getStatusColor(groupStatus)} text-white`}>
                                    {groupStatus.toUpperCase()}
                                </Badge>
                            </div>
                            <CardDescription>
                                Created: {new Date(group.createdAt).toLocaleDateString()}
                                <span className="mx-2">•</span>
                                {group.competitionIds?.length || 0} competitions
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => copyToClipboard(timerUrl, 'timer')}
                            >
                                {copiedResults ? (
                                    <Check className="size-4 text-green-600" />
                                ) : (
                                    <Copy className="size-4" />
                                )}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteGroup(group._id)}
                                disabled={isLoading}
                                title="Delete Group"
                            >
                                <Trash2 className="size-4" />
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                <CardContent>
                    {/* Simple competition list */}
                    <div className="space-y-3 mb-6">
                        {group.competitionIds.map((competitionId, index) => {
                            const competition = getCompetitionById(competitionId);
                            if (!competition) return null;

                            return (
                                <div key={competitionId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                    <div className="flex-1">
                                        <div className="font-medium">{competition.name}</div>
                                        <div className="text-sm text-gray-500 flex items-center gap-4 mt-1">
                                            <span className="flex items-center gap-1">
                                                <Users className="size-3" />
                                                {competition.competitors?.length || 0} climbers
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Trophy className="size-3" />
                                                {competition.numberofboulders || 0} boulders
                                            </span>
                                            {competition.timer?.status && (
                                                <Badge variant="outline" className={`${getStatusColor(competition.timer.status)} text-white border-0 text-xs`}>
                                                    {competition.timer.currentPhase || competition.timer.status}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Group controls - simplified */}
                    <div className="flex flex-wrap gap-2">
                        <Button
                            onClick={() => handleControlGroup(group, 'pause')}
                            variant="outline"
                            className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
                            disabled={isLoading || groupStatus === 'paused' || groupStatus === 'empty' || groupStatus === 'completed' || !hasWrapTimeCompetitions}
                            size="sm"
                        >
                            <Pause className="size-3 mr-1" />
                            Pause All
                        </Button>
                        <Button
                            onClick={() => handleControlGroup(group, 'resume')}
                            variant="outline"
                            className="border-green-500 text-green-700 hover:bg-green-50"
                            disabled={isLoading || groupStatus !== 'paused' || groupStatus === 'empty'}
                            size="sm"
                        >
                            <Play className="size-3 mr-1" />
                            Resume All
                        </Button>
                        {/* <Button
                            onClick={() => handleControlGroup(group, 'climbing')}
                            variant="outline"
                            className="border-blue-500 text-blue-700 hover:bg-blue-50"
                            disabled={isLoading}
                            size="sm"
                        >
                            <ChevronRight className="size-3 mr-1" />
                            Start Climbing
                        </Button> */}
                        {/* <Button
                            onClick={() => handleControlGroup(group, 'reset')}
                            variant="outline"
                            className="border-red-500 text-red-700 hover:bg-red-50"
                            disabled={isLoading}
                            size="sm"
                        >
                            <RotateCcw className="size-3 mr-1" />
                            Reset All
                        </Button> */}
                    </div>
                </CardContent>
            </Card>
        );
    };

    // Simplified competition card for selection
    const renderCompetitionCard = (competition) => {
        const isSelected = selectedCompetitions.includes(competition._id);

        const isInGroup = competitionGroups.some(group =>
            group.competitionIds?.includes(competition._id)
        );

        if (isInGroup) return null;

        return (
            <Card
                key={competition._id}
                className={`border-2 transition-all cursor-pointer hover:shadow-md ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => handleCompetitionSelect(competition._id)}
            >
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                {isSelected && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                )}
                                <h3 className="font-semibold text-lg">{competition.name}</h3>
                                <Badge className={`text-xs px-2 py-0.5 rounded-md ${getStatusColor(competition.status)}`}>
                                    {getStatusText(competition.status)}
                                </Badge>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                    <Users className="size-3" />
                                    <span>{competition.competitors?.length || 0} climbers</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Trophy className="size-3" />
                                    <span>{competition.numberofboulders || 0} boulders</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Calendar className="size-3" />
                                    <span>{new Date(competition.startDate).toLocaleDateString()}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <MapPin className="size-3" />
                                    <span>{competition.location || 'No location'}</span>
                                </div>
                            </div>
                        </div>

                        <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleCompetitionSelect(competition._id)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </CardContent>
            </Card>
        );
    };

    // Get competitions not in any group
    const availableCompetitions = competitions.filter(comp =>
        !competitionGroups.some(group => group.competitionIds?.includes(comp._id)) && comp.status == "upcoming"
    );
    console.log(competitionGroups);
    console.log(competitions, 'competitions');


    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 max-w-6xl mx-auto">
            <div className="space-y-8">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold">Multi-Competition Timer</h1>
                        <p className="text-gray-600">Manage groups of competitions running simultaneously</p>
                    </div>

                    <div className="flex gap-3">
                        <Button
                            onClick={handleStartNewGroup}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                        // disabled={isFetching}
                        >
                            <Plus className="size-4" />
                            New Group
                        </Button>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            variant="outline"
                        >
                            Back to Dashboard
                        </Button>
                    </div>
                </div>

                {/* Status Bar */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <Badge variant={isSocketConnected ? "default" : "secondary"}
                                    className={isSocketConnected ? "bg-green-500" : "bg-gray-500"}>
                                    {isSocketConnected ? "🟢 Connected" : "🔴 Disconnected"}
                                </Badge>
                                <span className="text-sm text-gray-600">
                                    {competitionGroups.length} active group(s)
                                </span>
                                <span className="text-sm text-gray-600">
                                    {availableCompetitions.length} competition(s) available
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Error Display */}
                {error && (
                    <Alert className="bg-red-50 border-red-200">
                        <AlertDescription className="text-red-700">
                            {error}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Competition Groups Section */}
                {competitionGroups.length > 0 ? (
                    <div className="space-y-4">
                        <h2 className="text-2xl font-bold">Active Groups</h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {competitionGroups.map(renderGroupCard)}
                        </div>
                    </div>
                ) : (
                    <Card className="border-2 border-dashed border-gray-300">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <div className="bg-blue-100 p-4 rounded-full mb-4">
                                <Clock className="size-12 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-600 mb-2">No groups yet</h3>
                            <p className="text-gray-500 text-center mb-6">
                                Create your first group to run multiple competitions simultaneously
                            </p>
                            <Button
                                onClick={handleStartNewGroup}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                                disabled={isFetching}
                            >
                                <Plus className="size-4" />
                                Create First Group
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Selection Dialog */}
                <Dialog open={showSelectionDialog} onOpenChange={setShowSelectionDialog}>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
                        <DialogHeader>
                            <DialogTitle>Select Competitions for New Group</DialogTitle>
                            <DialogDescription>
                                Choose competitions to run together as a group. All selected competitions will start simultaneously.
                            </DialogDescription>
                        </DialogHeader>

                        {availableCompetitions.length === 0 ? (
                            <div className="py-8 text-center">
                                <p className="text-gray-500">No competitions available for grouping.</p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                                    {availableCompetitions.map(renderCompetitionCard)}
                                </div>

                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowSelectionDialog(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                                        onClick={handleCreateGroup}
                                        disabled={selectedCompetitions.length === 0}
                                    >
                                        Next: Set Countdown ({selectedCompetitions.length} selected)
                                    </Button>
                                </DialogFooter>
                            </>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Countdown Dialog */}
                <StartDialog
                    open={showCountdownDialog}
                    onOpenChange={setShowCountdownDialog}
                    onStart={handleStartGroupCountdown}
                    loading={loading}
                    countdownDuration={countdownDuration}
                    setCountdownDuration={setCountdownDuration}
                />
            </div>
        </div>
    );
};

export default MultiCompetitionTimer;